import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

// A drag list inside a turbo-frame (the intern's page) reloads that frame
// from the page URL. If the session has expired, the reload lands on the
// sign-in page, which has no such frame: go to the whole page instead of
// writing "Content missing" into the list. Registered once per page load.
if (!window.__taskSortableFrameGuard) {
  window.__taskSortableFrameGuard = true
  document.addEventListener("turbo:frame-missing", (event) => {
    if (!event.target.hasAttribute("data-task-sortable-frame")) return
    event.preventDefault()
    event.detail.visit(event.detail.response)
  })
}

export default class extends Controller {
  static targets = ["list"]
  // internId: the Intern Tasks sheet sends whose list this is, because the
  // drag order is saved per intern there (2026-09-28). Other pages omit it.
  // refresh: turbo-frame ids (comma-separated) to re-read from the server
  // once the new order is saved. The intern's own page shows the same rows
  // in two lists (Today's Tasks, and every open task), so after a drop in
  // one the other is fetched again and shows the saved order (support
  // #1189, 2026-09-29). Other pages omit it.
  static values = { url: String, internId: String, refresh: String }

  connect() {
    this.initSortable()
  }

  disconnect() {
    this.sortable?.destroy()
  }

  initSortable() {
    this.sortable = new Sortable(this.listTarget, {
      animation: 150,
      handle: "[data-drag-handle]",
      ghostClass: "opacity-30",
      // Touch: press-and-hold to drag so a quick swipe still scrolls the page
      delay: 150,
      delayOnTouchOnly: true,
      // Rows that carry data-sort-block (the Intern Tasks sheet: due date
      // plus priority level) can only move among rows of the same block,
      // because the sheet sorts by day and then high priority first, so a
      // drop anywhere else would snap back on the next load. Rows without
      // it (every other page) move freely, as before.
      onMove: (evt) => {
        const block = evt.dragged.dataset.sortBlock
        return block === undefined || evt.related.dataset.sortBlock === block
      },
      // While dragging, fade every row outside the dragged row's block, so
      // the edge of the day is visible only when it matters (committee
      // 2026-09-28: an invisible wall reads as "drag is broken").
      onStart: (evt) => {
        // If this list's frame is being re-read after a drop in the other
        // list, cancel that read: it predates the drag starting now, and
        // landing afterwards would put back an order that is no longer saved
        // (support #1189 committee, 2026-09-29).
        const own = this.element.closest("turbo-frame")
        if (own?.hasAttribute("busy")) own.src = null
        // The rows as they were before this drag, so a failed save can put
        // them back on every page (owner 2026-09-29), hidden per-intern
        // expansion rows on the sheet included.
        this.snapshot = Array.from(this.listTarget.children)
        const block = evt.item.dataset.sortBlock
        if (block === undefined) return
        this.listTarget.querySelectorAll("[data-sort-block]").forEach((row) => {
          if (row.dataset.sortBlock !== block) row.classList.add("opacity-40")
        })
      },
      onEnd: this.onDragEnd.bind(this)
    })
  }

  async onDragEnd(evt) {
    // This drag's starting rows, kept here: a second drag started before
    // this save answers replaces this.snapshot.
    const before = this.snapshot
    this.listTarget.querySelectorAll("[data-sort-block].opacity-40").forEach((row) => row.classList.remove("opacity-40"))
    // Sortable fires this on every release, including a row put back where it
    // was or refused at a day's edge: nothing moved, so nothing to save.
    if (evt && evt.oldIndex === evt.newIndex) return
    // The Intern Tasks sheet: its own rows only. A shared task's hidden
    // per-intern lines are a separate sibling that stays behind when the row
    // moves, and their status pills carry data-task-id too. Counting them
    // lost a shared row's drag: the shipped code let the last appearance
    // win (a drag UP saved the old place), and this branch's first cut let
    // the first win (a drag DOWN did), caught in committee review 2026-09-28.
    // Other pages: every data-task-id, once per task, first appearance (the
    // Team Tasks page wraps each row in another element).
    // Only the dragged row's own block (day and priority level) is posted:
    // positions are compared only inside a block, and a list that does not
    // show every row (the intern's page hides finished ones) would otherwise
    // renumber days nobody touched and reshuffle them on the sheet
    // (support #1189 committee, 2026-09-29).
    const sheetRows = this.listTarget.querySelectorAll(":scope > [data-sort-block]")
    const block = evt?.item?.dataset?.sortBlock
    const rows = sheetRows.length
      ? Array.from(sheetRows).filter((row) => block === undefined || row.dataset.sortBlock === block)
      : this.listTarget.querySelectorAll("[data-task-id]")
    const taskIds = [...new Set(Array.from(rows).map(row => row.dataset.taskId))]

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({ task_ids: taskIds, intern_id: this.hasInternIdValue ? this.internIdValue : undefined })
      })

      if (!response.ok) throw new Error("Reorder failed")
      this.refreshFrames()
    } catch (error) {
      console.error("Failed to save task order:", error)
      // Only while this list is still on screen: a slow failure after the
      // staffer moved to another page must not pop up there.
      if (!this.element.isConnected) return
      this.putBack(this.listTarget, before, evt?.item)
      if (window.showToast) window.showToast("error", "Could not save the new order. Nothing was changed.")
      // Inside a frame (the intern's page), put the list back to the order
      // actually saved, so the page never shows an order that did not save.
      const own = this.element.closest("turbo-frame")
      if (own) this.reloadFrame(own)
    }
  }

  // Re-fetch the named turbo-frames from the current page URL. A frame that
  // is not on the page is skipped, and so is the frame this list sits in:
  // its rows already show the new order.
  refreshFrames() {
    if (!this.hasRefreshValue) return
    this.refreshValue.split(",").forEach((id) => {
      const frame = document.getElementById(id.trim())
      if (!frame || frame === this.element.closest("turbo-frame")) return
      this.reloadFrame(frame)
    })
  }

  // The frames render inline (no src of their own), so setting src is what
  // makes Turbo fetch the page and swap in the frame's fresh rows; clearing
  // it first also cancels a fetch still in flight from an earlier drop.
  reloadFrame(frame) {
    frame.src = null
    frame.src = window.location.href
  }

  // Put back only the dragged row, before the first row that followed it
  // when the drag began and is still in the list. Not the whole snapshot:
  // on phones Sortable's floating copy of the row is in that snapshot (it is
  // removed on drop), and a row taken off the page meanwhile must stay off
  // (committee 2026-09-29). The other rows never moved.
  putBack(list, before, item) {
    if (!before || !item) return
    if (item.parentNode !== list) return   // taken off the page meanwhile
    const i = before.indexOf(item)
    const next = before.slice(i + 1).find((el) => el !== item && el.parentNode === list)
    list.insertBefore(item, next || null)
  }
}
